import { route, SandboxError, type Route } from '../adapter';
import { newId, now } from '../store';
import type { Template, TemplateUsageView } from '@/lib/templates';
import { personName, type EventPerson } from '@/lib/events';

/** Note templates. Changing the fields bumps the version; notes keep their values. */

type TemplateWrite = Omit<Template, '_id' | 'version' | 'usageCount' | 'updatedAt'>;

export const templateRoutes: readonly Route[] = [
  route('GET', '/templates', ({ store }) => ({ data: store.templates })),
  route('POST', '/templates', ({ store, body }) => {
    const write = body as TemplateWrite;
    if (store.templates.some((template) => template.key === write.key)) throw new SandboxError(409, 'A template with that key already exists.');
    const template: Template = { ...write, _id: newId(), version: 1, usageCount: 0, updatedAt: now() };
    store.templates = [...store.templates, template];
    return { data: template };
  }),
  route('PATCH', '/templates/:id', ({ store, params, body }) => {
    const index = store.templates.findIndex((row) => row._id === params['id']);
    const existing = store.templates[index];
    if (!existing) throw new SandboxError(404, 'Template not found.');
    const patch = body as Partial<TemplateWrite>;
    const fieldsChanged = patch.fields !== undefined && JSON.stringify(patch.fields) !== JSON.stringify(existing.fields);
    const updated: Template = { ...existing, ...patch, version: existing.version + (fieldsChanged ? 1 : 0), usageCount: store.notes.filter((note) => note.templateKey === existing.key).length, updatedAt: now() };
    store.templates[index] = updated;
    return { data: updated };
  }),
  // Every sandbox note is on the sandbox's one event.
  route('GET', '/templates/:id/usage', ({ store, params }) => {
    const template = store.templates.find((row) => row._id === params['id']);
    if (!template) throw new SandboxError(404, 'Template not found.');
    const author = (owner: EventPerson | string | undefined): { id: string; name: string } | null =>
      owner && typeof owner !== 'string' ? { id: owner._id, name: personName(owner) } : null;
    const notes = store.notes
      .filter((note) => note.templateKey === template.key && !note.archivedAt)
      .map((note) => ({
        noteId: note._id,
        subject: note.subject,
        category: note.category ?? '',
        ...(note.status ? { status: note.status } : {}),
        ...(note.updatedAt ? { updatedAt: note.updatedAt } : {}),
        author: author(note.owner),
        event: {
          id: store.event._id,
          name: store.event.opportunityName ?? store.event.account?.name ?? 'Sandbox event',
          ...(store.event.contractNumber ? { contractNumber: store.event.contractNumber } : {}),
          ...(store.event.eventDate ? { eventDate: store.event.eventDate } : {}),
        },
      }));
    const view: TemplateUsageView = { template: { _id: template._id, key: template.key, name: template.name }, total: notes.length, limit: notes.length, notes };
    return { data: view };
  }),
  route('DELETE', '/templates/:id', ({ store, params }) => {
    store.templates = store.templates.filter((row) => row._id !== params['id']);
    return { data: null };
  }),
];
